import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Building, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const RegisterCentroPage = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', ragione_sociale: '', vat_number: '',
    region: '', province: '', city: '', comune: '', phone: '', bio: '',
  });

  useEffect(() => {
    api.get('/api/geo/italian-regions').then((r) => setRegions(r.data || [])).catch(() => {});
  }, []);

  const onRegion = (region) => {
    setForm((f) => ({ ...f, region, province: '' }));
    const row = regions.find((r) => r.region === region);
    setProvinces(row?.provinces || []);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.ragione_sociale || !form.vat_number || !form.region || !form.city) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    if (!/^[A-Za-z0-9]{8,16}$/.test(form.vat_number.trim())) {
      toast.error('P.IVA non valida (8-16 caratteri alfanumerici)');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/api/auth/register-centro', {
        ...form,
        vat_number: form.vat_number.trim(),
      });
      authLogin(r.data.token, r.data.user);
      toast.success('Registrazione Centro effettuata! In attesa di approvazione.');
      navigate('/professional-dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore registrazione');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="neumorphism rounded-2xl p-8" data-testid="register-centro-card">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building size={32} weight="duotone" className="text-primary" />
            </div>
            <h1 className="font-heading font-semibold text-3xl mb-2">Registra il tuo Centro</h1>
            <p className="text-foreground/70 text-sm">
              Centro di Assistenza Domiciliare — copertura multi-categoria e multi-operatore.
            </p>
          </div>

          <div className="mb-4 p-3 rounded-lg bg-primary/5 text-xs text-foreground/70 flex items-start gap-2">
            <CheckCircle size={14} className="text-primary shrink-0 mt-0.5" />
            <span>Di default il Centro appare in <strong>tutte le categorie</strong> dei professionisti. Potrai disabilitare quelle che non offri dal tuo pannello &quot;Prestazioni&quot;.</span>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="centro-form">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Ragione Sociale *</Label>
                <Input value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} required data-testid="input-ragione-sociale" />
              </div>
              <div className="space-y-1">
                <Label>Partita IVA *</Label>
                <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} required data-testid="input-piva" />
              </div>
              <div className="space-y-1">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="input-email" />
              </div>
              <div className="space-y-1">
                <Label>Password *</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="input-password" />
              </div>
              <div className="space-y-1">
                <Label>Regione *</Label>
                <select
                  value={form.region}
                  onChange={(e) => onRegion(e.target.value)}
                  className="w-full h-10 border border-border rounded-md px-3 bg-background"
                  data-testid="select-region"
                  required
                >
                  <option value="">Seleziona…</option>
                  {regions.map((r) => <option key={r.region} value={r.region}>{r.region}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Provincia</Label>
                <select
                  value={form.province}
                  onChange={(e) => setForm({ ...form, province: e.target.value })}
                  disabled={!provinces.length}
                  className="w-full h-10 border border-border rounded-md px-3 bg-background disabled:opacity-50"
                  data-testid="select-province"
                >
                  <option value="">Seleziona…</option>
                  {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Città *</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required data-testid="input-city" />
              </div>
              <div className="space-y-1">
                <Label>Comune (opzionale)</Label>
                <Input value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} data-testid="input-comune" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Telefono</Label>
                <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="input-phone" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Descrizione (opzionale)</Label>
                <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Servizi offerti, orari, coperture…" data-testid="input-bio" />
              </div>
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={saving} data-testid="submit-centro-btn">
              {saving ? 'Registrazione…' : 'Registra Centro'}
            </Button>
            <p className="text-xs text-center text-foreground/60">
              Hai già un account? <Link to="/login" className="text-primary underline">Accedi</Link>
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default RegisterCentroPage;
